import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

type Kind = "stock" | "semi" | "finished";

/** TR sayı: 1.234 / 1.234,56 → nokta binlik, virgül ondalık (CreateTransferDrawer.parseTrNumber ile aynı). */
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}
function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }

interface Line { key: string; itemKind: Kind; itemId: string; itemName: string; itemCode: string; qty: number; uom: string; available: number; lotId: string | null; lotCode: string | null; }

/** Yeni iç transfer talebi (lojistiksiz, çift-onaylı): kaynak/hedef depo + çok-satır ürün+miktar (FEFO / opsiyonel lot). */
export function InternalTransferDrawer({ open, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const locsQ = trpc.internalTransfer.stockLocations.useQuery(undefined, { enabled: open });
  const createMut = trpc.internalTransfer.create.useMutation();

  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  // add-row
  const [pickItem, setPickItem] = useState<string | null>(null); // "kind:id"
  const [pickQty, setPickQty] = useState("");
  const [pickLot, setPickLot] = useState(""); // "" = FEFO
  const [err, setErr] = useState<string | null>(null);

  const sourceQ = trpc.internalTransfer.sourceStock.useQuery({ fromLocationId: fromId ?? "" }, { enabled: open && !!fromId });
  const [pkind, pid] = (pickItem ?? ":").split(":") as [Kind, string];
  const lotsQ = trpc.internalTransfer.sourceLots.useQuery(
    { fromLocationId: fromId ?? "", itemKind: pkind, itemId: pid },
    { enabled: open && !!fromId && !!pickItem }
  );

  useEffect(() => {
    if (open) { setFromId(null); setToId(null); setNotes(""); setLines([]); setPickItem(null); setPickQty(""); setPickLot(""); setErr(null); createMut.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { setLines([]); setPickItem(null); setPickQty(""); setPickLot(""); }, [fromId]);
  useEffect(() => { setPickLot(""); }, [pickItem]);

  const locOpts = useMemo(() => (locsQ.data ?? []).map((l) => ({ value: l.id, label: l.name, desc: l.code })), [locsQ.data]);
  const itemOpts = useMemo(
    () => (sourceQ.data ?? []).map((s) => ({ value: `${s.itemKind}:${s.itemId}`, label: s.name, desc: s.code, tail: `${fmtQty(s.available)} ${s.uom}` })),
    [sourceQ.data]
  );
  const lotOpts = useMemo(() => {
    // Bu ürün için zaten eklenmiş elle-lotları gizle (kardeş CreateTransferDrawer 'taken' deseni).
    const taken = new Set(lines.filter((l) => `${l.itemKind}:${l.itemId}` === pickItem && l.lotId).map((l) => l.lotId));
    return [
      { value: "", label: "FEFO (otomatik)", desc: "en erken SKT'den seçilir" },
      ...(lotsQ.data ?? []).filter((l) => !taken.has(l.lotId)).map((l) => ({ value: l.lotId, label: l.lotCode, desc: "elle lot", tail: `${fmtQty(l.qty)} ${l.uom}` })),
    ];
  }, [lotsQ.data, lines, pickItem]);

  function addLine() {
    setErr(null);
    if (!pickItem) return;
    const src = sourceQ.data?.find((s) => `${s.itemKind}:${s.itemId}` === pickItem);
    if (!src) return;
    const lot = pickLot ? lotsQ.data?.find((l) => l.lotId === pickLot) : null;
    const cap = lot ? lot.qty : src.available;
    const n = pickQty.trim() ? parseTrNumber(pickQty) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin.");
    if (n > cap + 0.0001) return setErr(`En fazla ${fmtQty(cap)} ${src.uom} mevcut${lot ? " (seçili lot)" : ""}.`);
    const dupKey = `${src.itemKind}:${src.itemId}:${pickLot}`;
    if (lines.some((l) => `${l.itemKind}:${l.itemId}:${l.lotId ?? ""}` === dupKey)) return setErr("Aynı ürün/lot zaten eklendi.");
    // Aynı ürün için FEFO + elle-lot karışımı yasak (backend de reddeder) — onayda çift-seçim/yersiz red önlenir.
    const forItem = lines.filter((l) => `${l.itemKind}:${l.itemId}` === `${src.itemKind}:${src.itemId}`);
    if ((!pickLot && forItem.some((l) => l.lotId)) || (pickLot && forItem.some((l) => !l.lotId))) return setErr("Aynı ürün için hem FEFO hem elle lot olamaz — ya tek FEFO ya belirli lotlar seçin.");
    setLines((p) => [...p, { key: `${dupKey}:${Math.random().toString(36).slice(2, 7)}`, itemKind: src.itemKind, itemId: src.itemId, itemName: src.name, itemCode: src.code, qty: n, uom: src.uom, available: src.available, lotId: lot ? lot.lotId : null, lotCode: lot ? lot.lotCode : null }]);
    setPickItem(null); setPickQty(""); setPickLot("");
  }
  function removeLine(key: string) { setLines((p) => p.filter((l) => l.key !== key)); }

  async function submit() {
    setErr(null);
    if (!fromId || !toId) return setErr("Kaynak ve hedef depo seçin.");
    if (fromId === toId) return setErr("Kaynak ve hedef depo aynı olamaz.");
    if (lines.length === 0) return setErr("En az bir ürün satırı ekleyin.");
    try {
      const res = await createMut.mutateAsync({
        fromLocationId: fromId, toLocationId: toId,
        notes: notes.trim() || undefined,
        lines: lines.map((l) => ({ itemKind: l.itemKind, itemId: l.itemId, qty: l.qty, lotId: l.lotId })),
      });
      await qc.invalidateQueries();
      onClose();
      onCreated(res.id);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer open={open} onClose={onClose} size="lg" title="Yeni İç Transfer" subtitle="Depolar arası (lojistiksiz) · onay bekleyen talep oluşturur"
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
        <button type="button" className="btn-md primary" onClick={submit} disabled={createMut.isPending || !fromId || !toId || lines.length === 0}><Icon name="plus" size={14} />{createMut.isPending ? "Oluşturuluyor…" : "Talep Oluştur"}</button>
      </>}>
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-row" style={{ gap: 12 }}>
          <div className="form-field"><label className="lbl">Kaynak Depo <span className="req">*</span></label>
            <SearchSelect value={fromId} options={locOpts.filter((o) => o.value !== toId)} onChange={setFromId} icon="warehouse" placeholder="Kaynak depo seç…" searchPlaceholder="Depo ara…" emptyText="Stok deposu yok" />
          </div>
          <div className="form-field"><label className="lbl">Hedef Depo <span className="req">*</span></label>
            <SearchSelect value={toId} options={locOpts.filter((o) => o.value !== fromId)} onChange={setToId} icon="warehouse" placeholder="Hedef depo seç…" searchPlaceholder="Depo ara…" emptyText="Stok deposu yok" />
          </div>
        </div>
        <div className="form-field"><label className="lbl">Not</label><textarea className="form-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} placeholder="Transfer notu (opsiyonel)" /></div>

        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
            <span className="mo-sec-title" style={{ fontSize: 12.5, fontWeight: 600 }}>Taşınacak Ürünler ({lines.length}) <span className="req">*</span></span>
            <span style={{ fontSize: 11, color: "var(--neutral-500)" }}>onaylandığında stok kaynak→hedef taşınır (FEFO)</span>
          </div>
          {lines.length > 0 ? (
            <table className="dt2" style={{ marginBottom: 8 }}>
              <thead><tr><th>Ürün</th><th style={{ width: 150 }}>Lot</th><th className="num" style={{ width: 130 }}>Miktar</th><th style={{ width: 40 }} /></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key}>
                    <td>{l.itemName}<span style={{ fontSize: 10.5, color: "var(--neutral-400)", marginLeft: 6, fontFamily: "var(--font-mono)" }}>{l.itemCode}</span></td>
                    <td>{l.lotCode ? <span className="cell-id" style={{ fontSize: 11 }}>{l.lotCode}</span> : <span className="badge-pill b-info"><Icon name="zap" size={10} />FEFO</span>}</td>
                    <td className="cell-num">{fmtQty(l.qty)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{l.uom}</span></td>
                    <td><div className="row-acts"><button type="button" className="iact danger" aria-label="Satırı kaldır" onClick={() => removeLine(l.key)}><Icon name="x" size={14} /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: "8px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", marginBottom: 8 }}>
              {fromId ? "Kaynak depodaki ürünlerden ekleyin (en az bir satır)." : "Önce kaynak depo seçin."}
            </div>
          )}
          {fromId ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 180 }}><SearchSelect value={pickItem} options={itemOpts} onChange={setPickItem} icon="boxes" placeholder="Kaynak depodaki ürün…" searchPlaceholder="Ürün / kod ara…" emptyText={sourceQ.isLoading ? "Yükleniyor…" : "Kaynak depoda taşınabilir stok yok"} /></div>
              {pickItem ? (
                <div style={{ flex: "1 1 160px", minWidth: 150 }}><SearchSelect value={pickLot} options={lotOpts} onChange={(v) => setPickLot(v ?? "")} icon="cube" placeholder="Lot (FEFO)" searchPlaceholder="Lot ara…" emptyText="Lot yok" /></div>
              ) : null}
              <input className="form-input" style={{ width: 96 }} type="text" inputMode="decimal" value={pickQty} onChange={(e) => setPickQty(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="miktar" aria-label="Miktar" />
              <button type="button" className="btn-md secondary" onClick={addLine} disabled={!pickItem}><Icon name="plus" size={14} />Ekle</button>
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
